// vuex store for submodule: School Quality Snapshot (Year-specific)

import * as d3 from 'd3-collection'

import { SQSAllDataApi, SQSAllDataPreviewApi } from '@/api/data/sqs'
import { d3NestToMapRecurse, valIsMissing, boroughForDbn, isCharterDbn,
  yearRangeForReportYear,
  schoolTypeCodeForName, schoolIsDistrictPK,
  sqgURL, scoringGuideURL, qrURL, dashboardURL, schoolFinderURL, composePanoramaURL,
  surveyRecognitionURL
} from '@/utils/data'
import { SQS_COVID_YEARS, SQS_NO_PK_YEAR, SQS_PK_SECRET_KEYS
} from '@/report-settings'

const ELEMENT_CODES = ['IP', 'SS', 'RF']
const ALL_TAB_CODES = ['INFO'].concat(ELEMENT_CODES)
// 2024: EC and D75 will display 'No Rating' in all three framework ratings (suppression on data side)
const NO_RATINGS_SCHOOL_TYPES = ['EC', 'D75']

// initial state
const state = {
  reportYear: undefined,
  isEmbargoed: true,
  layoutMetadata: undefined,
  schoolInfo: undefined,
  schoolInfoPK: undefined,
  selectedSchool: {},
  selectedSchoolData: undefined
}

// getters
const getters = {
  reportYear: state => state.reportYear,
  isEmbargoed: state => state.isEmbargoed,
  layoutMetadata: state => state.layoutMetadata,
  schoolInfo: state => state.schoolInfo,
  schoolInfoPK: state => state.schoolInfoPK,
  isSchoolInfoLoaded: state => (state.schoolInfo !== undefined && state.schoolInfoPK !== undefined),
  isSchoolDataLoaded: state => state.selectedSchoolData !== undefined,
  selectedSchoolData: state => state.selectedSchoolData,

  selectedSchool: state => state.selectedSchool,
  selectedSchoolType: state => schoolTypeCodeForName(state.selectedSchool.school_type),
  selectedSchoolName: state => state.selectedSchool.school || '',

  hasSelectedSchool: state => state.selectedSchool.dbn !== undefined && state.selectedSchool.report_type !== undefined,
  isSelectedSchoolCharter: state => isCharterDbn(state.selectedSchool.dbn),
  isSelectedSchoolPK: state => state.selectedSchool.report_type === 'PK',
  isSelectedSchoolDistrictPK: (state, getters) => schoolIsDistrictPK(getters.selectedSchool),
  isSelectedSchoolNYCEECPK: (state, getters) => getters.isSelectedSchoolPK && state.selectedSchool.school_type === 'NYCEEC',
  isCovidYear: (state, getters) => SQS_COVID_YEARS.indexOf(getters.reportYear) > -1,

  layoutMetadataForSchoolType: (state) => (schoolType) => {
    return state.layoutMetadata ? state.layoutMetadata[schoolType] || {} : {}
  },

  schoolListAll: (state, getters) => {
    if (!getters.isSchoolInfoLoaded) return undefined

    const combinedList = state.schoolInfoPK.concat(state.schoolInfo)
    combinedList.sort(function (a, b) {
      if (a.dbn > b.dbn) {
        return 1
      }
      if (a.dbn < b.dbn) {
        return -1
      }
      return a.report_type === 'PK' ? 1 : -1
    })
    return combinedList
  },
  schoolListAuthorized: (state, getters, rootState, rootGetters) => {
    const schoolList = getters.schoolListAll
    const isSPAdmin = rootGetters['isSPAdmin']
    const isLoggedIn = rootGetters['isLoggedIn']
    const authDBNs = rootGetters['authorizedDBNs']
    if (!getters.isSchoolInfoLoaded) {
      return undefined
    }
    if (!state.isEmbargoed || isSPAdmin) {
      return schoolList
    }
    if (!isLoggedIn) {
      if (SQS_NO_PK_YEAR === state.reportYear) {
        return schoolList.filter(ele => ele.report_type === 'PK')
      }

      return undefined
    }
    return schoolList.filter(s => authDBNs.indexOf(s.dbn) > -1)
  },
  // Flag for if user is trying to access embargoed report without permissions
  isUnauthorizedForEmbargo: (state, getters, rootState, rootGetters) => {
    const isLoggedIn = rootGetters['isLoggedIn']
    if (!state.isEmbargoed || !getters.isSchoolInfoLoaded || !isLoggedIn) {
      // Not embargoed, or don't know what auth status is yet
      return false
    }
    return getters.schoolListAuthorized.length === 0
  },

  // Custom getters for School Data
  valueForMetric: (state, getters) => (metricName) => {
    const d = state.selectedSchoolData
    return getters.isSchoolDataLoaded ? d[metricName] : undefined
  },
  elementRatingForCode: (state, getters) => (elementCode) => {
    // uncomment below to mimic data side suppression of rating_ip, rating_ss, rating_rf metrics for EC and D75 school types and display 'No Rating'
    // if (getters.isECorD75SchoolType) return null // or return ''
    // May be string or numeric, since it may be 'N/A'
    const ec = elementCode.toLowerCase()
    return getters.valueForMetric(`rating_${ec}`)
  },
  selectedSchoolNameLong: (state, getters) => {
    return getters.valueForMetric('location_name_long')
  },
  selectedSchoolPKCode: (state, getters) => {
    return getters.valueForMetric('program_code')
  },
  selectedSchoolDisplayName: (state, getters) => {
    const selectedSchool = getters.selectedSchool
    const selectedSchoolName = getters.selectedSchoolName
    const isPK = getters.isSelectedSchoolPK
    const isDistrictPK = getters.isSelectedSchoolDistrictPK

    const schoolName = getters.selectedSchoolNameLong || selectedSchoolName
    const dbn = selectedSchool ? selectedSchool.dbn : undefined
    const code = isPK ? getters.selectedSchoolPKCode : dbn
    const codeStr = code ? ` (${code})` : ''
    const pkStr = isDistrictPK ? `—Pre-K Program` : ''

    return schoolName ? `${schoolName}${codeStr}${pkStr}` : ''
  },
  selectedSchoolDistrict: (state, getters) => {
    return getters.valueForMetric('district')
  },
  selectedSchoolBorough: (state, getters) => {
    return getters.valueForMetric('borough_name')
  },
  selectedSchoolGrades: (state, getters) => {
    return getters.valueForMetric('grades_text')
  },
  selectedSchoolHasPK (state, getters) {
    /* tests if PK is in the grades string. \b stands for boundary */
    return /\bPK\b/.test(getters.selectedSchoolGrades)
  },
  selectedSchoolQRYear: (state, getters) => {
    return getters.valueForMetric('quality_review_year')
  },
  hasQRReview: (state, getters) => {
    return !valIsMissing(getters.valueForMetric('quality_review_year'))
  },
  isHighOptOutSchool: (state, getters) => {
    return +getters.valueForMetric('high_opt_out_sch') === 1
  },
  isNoGraduatingClassSchool: (state, getters) => {
    return +getters.valueForMetric('no_graduating_class') === 1
  },
  isNewOrPhaseoutSchool: (state, getters) => {
    return +getters.valueForMetric('is_new_school') === 1 || +getters.valueForMetric('is_phaseout_school')
  },
  isECorD75SchoolType: (state, getters) => {
    const selectedSchoolType = getters.selectedSchoolType
    return NO_RATINGS_SCHOOL_TYPES.indexOf(selectedSchoolType) > -1
  },
  isSurveyRecognitionSchool: (state, getters) => {
    return +getters.valueForMetric('survey_recognition') === 1
  },
  suppress_ss: (state, getters) => {
    return +getters.valueForMetric('suppress_ss') === 1
  },
  suppress_rf: (state, getters) => {
    return +getters.valueForMetric('suppress_rf') === 1
  },
  // when it's set, 'defaultText' applies to all cases when there is no footnote
  noRatingTextForCode: (state, getters) => (elementCode, defaultText) => {
    if (elementCode === 'IP' && (getters.isHighOptOutSchool || getters.isNoGraduatingClassSchool) && !getters.isNewOrPhaseoutSchool && !getters.isECorD75SchoolType) {
      return 'No Rating†'
    } else if (((elementCode === 'SS' && getters.suppress_ss) || (elementCode === 'RF' && getters.suppress_rf)) && !getters.isNewOrPhaseoutSchool && !getters.isECorD75SchoolType) {
      return 'No Rating‡'
    }
    return defaultText || 'No Rating'
  },

  selectedSchoolElementCodes: (state, getters) => {
    return ELEMENT_CODES
  },
  selectedSchoolTabCodes: (state, getters) => {
    return ALL_TAB_CODES
  },

  // URLs
  schoolFinderURL: (state, getters) => {
    return schoolFinderURL(getters.selectedSchool)
  },
  qualityReviewURL: (state, getters) => {
    return qrURL(getters.selectedSchool, getters.selectedSchoolQRYear)
  },
  guideURL: (state, getters) => {
    return sqgURL(getters.reportYear, getters.selectedSchool)
  },
  scoringGuideURL: (state, getters) => {
    return scoringGuideURL(getters.reportYear, getters.selectedSchool)
  },
  panoramaURL: (state, getters) => {
    // 30533 is project_id Panorama uses for year 2025
    return composePanoramaURL(getters.valueForMetric('panorama_id'), '30533')
  },
  dashboardURL: (state, getters) => {
    return dashboardURL(getters.selectedSchool)
  },
  surveyRecognitionURL: (state, getters) => {
    return surveyRecognitionURL(getters.reportYear)
  }

}

// actions
const actions = {
  async getLayoutMetadata (context, url) {
    const data = await context.dispatch('getDsvData', url, {root: true})
      .then(d => d.filter(e => !/-hidden$/.test(e.group_name)))

    const tmpData = d3.nest()
      .key(d => d.school_type)
      .key(d => d.tab_code)
      .key(d => d.col_num)
      .key(d => d.group_name)
      .key(d => d.component_index)
      .entries(data)
    const processedData = d3NestToMapRecurse(tmpData, 2)

    context.commit('setLayoutMetadata', processedData)
  },
  async getSchoolInfo (context, url) {
    const data = await context.dispatch('getDsvData', url, {root: true})
    context.commit('setSchoolInfo', data)
  },
  async getSchoolInfoPK (context, url) {
    if (context.getters.isEmbargoed && !SQS_PK_SECRET_KEYS[SQS_NO_PK_YEAR]) {
      context.commit('setSchoolInfoPK', [])
      return
    }

    const data = await context.dispatch('getDsvData', url, {root: true})
    context.commit('setSchoolInfoPK', data)
  },
  /**
   * @param {Object} apiParams - params to be passed to the api abstraction
   */
  async updateSelectedSchoolData (context, apiParams) {
    context.commit('clearSelectedSchoolData')
    const isPK = context.getters.isSelectedSchoolPK
    const apiClass = context.state.isEmbargoed && !isPK ? SQSAllDataPreviewApi : SQSAllDataApi
    // eslint-disable-next-line
    const dataApi = new apiClass(apiParams)
    const data = await context.dispatch('getApiData', dataApi, {root: true})

    const accumulator = (a, d) => { a[d.varname] = d.value; return a }
    let schoolData = data.reduce(accumulator, {})

    const y = apiParams['year']
    schoolData['year'] = y
    schoolData['year_range'] = yearRangeForReportYear(y)
    schoolData['year_minus_two'] = y - 2
    schoolData['borough_name'] = boroughForDbn(apiParams['dbn']) || ''

    context.commit('setSelectedSchoolData', schoolData)
  }
}

// mutations
const mutations = {
  initReportYear (state, year) {
    // Should in theory only be called once for setting up module by parent
    state.reportYear = +year
  },
  setIsEmbargoed (state, flag) {
    state.isEmbargoed = flag
  },
  setLayoutMetadata (state, data) {
    state.layoutMetadata = data
  },
  setSchoolInfo (state, data) {
    state.schoolInfo = data
  },
  setSchoolInfoPK (state, data) {
    state.schoolInfoPK = data
  },
  setSelectedSchool (state, school) {
    state.selectedSchool = school
  },
  clearSelectedSchool (state) {
    state.selectedSchool = {}
  },
  setSelectedSchoolData (state, data) {
    state.selectedSchoolData = data
  },
  clearSelectedSchoolData (state) {
    state.selectedSchoolData = undefined
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/sqs/2025/index.js